import React, { Dispatch, FC, Fragment, useState } from 'react';
import { Delete } from '@mui/icons-material';
import { IconButton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { SetDraftResponse } from 'api/Responses/SetResponses';
import Button from 'components/Button';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import SetModal from 'components/SetModal/SetModal';
import TrayDetails from 'components/TrayDetails/TrayDetails';
import { useLanguage } from 'context/AppContext';
import { draftActions, SetAction } from 'reducers/DraftReducer/draftActions';
import PartTable from './PartTable';
import SubTableWrapper from './SubTableWrapper';
import { TrayHeader } from './Table.styles';

interface TrayTableProps {
  set: SetDraftResponse;
  dispatchSet: Dispatch<SetAction>;
  disabled: boolean;
}

const TrayTable: FC<TrayTableProps> = ({ set, dispatchSet, disabled }) => {
  const [language] = useLanguage();
  const [open, setOpen] = useState(false);
  const [trayToDelete, setTrayToDelete] = useState('');
  // addDevicesTray is kept after closing so the modal content stays stable while it fades out
  const [addDevicesTray, setAddDevicesTray] = useState<string>();
  const [addDevicesOpen, setAddDevicesOpen] = useState(false);

  return (
    <Table stickyHeader>
      {Object.keys(set.trays).map(trayId => (
        <Fragment key={trayId}>
          <TableHead>
            <TableRow id={trayId}>
              <TableCell align="left" className="tray-header">
                <TrayHeader>
                  {trayId}
                  {!disabled && (
                    <Button
                      size="small"
                      sx={{ ml: 'auto', mr: '1rem', height: '2rem' }}
                      onClick={() => {
                        setAddDevicesTray(trayId);
                        setAddDevicesOpen(true);
                      }}
                    >
                      {language.TrayTable.addDevices}
                    </Button>
                  )}
                  <IconButton
                    onClick={() => {
                      setTrayToDelete(trayId);
                      setOpen(true);
                    }}
                    disabled={disabled}
                  >
                    <Delete />
                  </IconButton>
                </TrayHeader>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow className="tray-row">
              <TableCell>
                <TrayDetails
                  set={set}
                  trayId={trayId}
                  dispatchDraft={dispatchSet}
                  disabled={disabled}
                  isViewOnly={set.is_view_only}
                />
              </TableCell>
            </TableRow>
            <SubTableWrapper>
              <PartTable
                trayID={trayId}
                parts={set.trays[trayId].parts}
                trays={set.trays}
                dispatchDraft={dispatchSet}
                disabled={disabled}
              />
            </SubTableWrapper>
          </TableBody>
          <ConfirmModal
            open={open}
            close={() => setOpen(false)}
            confirm={() => dispatchSet(draftActions.trayDelete(trayToDelete))}
            confirmText={language.ConfirmModal.tray}
            buttonText={language.ConfirmModal.delete}
          />
        </Fragment>
      ))}
      <SetModal
        key={addDevicesTray}
        open={addDevicesOpen}
        close={() => setAddDevicesOpen(false)}
        draft={set}
        dispatchDraft={dispatchSet}
        initialTray={addDevicesTray}
      />
    </Table>
  );
};

const MemoizedTrayTable = React.memo(TrayTable);

export default MemoizedTrayTable;
